import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, MapPin, Video, Phone, MessageCircle, Mail, Loader2, Inbox, Trash2, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bb_admin_token";

const PREF_META = {
  call: { icon: Phone, label: "Wants a call back" },
  message: { icon: MessageCircle, label: "Wants a message" },
  email: { icon: Mail, label: "Wants an email" },
};

const inputCls =
  "w-full rounded-xl border border-[#E2DACD] bg-white px-4 py-3 text-sm text-[#1E2723] placeholder:text-[#A8B4AD] focus:border-[#527363]";

const NoteEditor = ({ enquiry, authHeaders, onSaved }) => {
  const [note, setNote] = useState(enquiry.notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = note !== (enquiry.notes || "");

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/enquiries/${enquiry.id}`, { notes: note }, { headers: authHeaders() });
      onSaved(enquiry.id, note);
      toast.success("Note saved — only you can see it.");
    } catch (e) {
      toast.error("Could not save the note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-[#B5A793] bg-[#F7F3EB] p-4" data-testid={`admin-notes-${enquiry.id.slice(0, 8)}`}>
      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#8A6A2F]">
        <Lock size={11} /> Your private notes — clients never see this
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="e.g. first session booked for Friday 10am, mentioned work stress…"
        className="mt-2 w-full rounded-lg border border-[#E2DACD] bg-white px-3 py-2 text-sm text-[#1E2723] placeholder:text-[#A8B4AD] focus:border-[#527363]"
        data-testid={`admin-notes-input-${enquiry.id.slice(0, 8)}`}
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          data-testid={`admin-notes-save-${enquiry.id.slice(0, 8)}`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#527363] px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#435F52] disabled:opacity-50"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save note
        </button>
      )}
    </div>
  );
};

const Admin = () => {
  usePageTitle("Enquiries — Private", "Private admin area.");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [payments, setPayments] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const authHeaders = (t) => ({ Authorization: `Bearer ${t || token}` });

  const load = async (t) => {
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: authHeaders(t) });
      setEnquiries(res.data);
      const pay = await axios.get(`${API}/payments`, { headers: authHeaders(t) });
      setPayments(pay.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load enquiries. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back, Brian.");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
    setPassword("");
  };

  const toggleHandled = async (q) => {
    setBusyId(q.id);
    try {
      await axios.patch(`${API}/enquiries/${q.id}`, { handled: !q.handled }, { headers: authHeaders() });
      setEnquiries((list) => list.map((e) => (e.id === q.id ? { ...e, handled: !q.handled } : e)));
      toast.success(q.handled ? "Marked as open again." : `Marked as responded — nice work.`);
    } catch (e) {
      toast.error("Could not update. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete the enquiry from ${q.name}? This cannot be undone.`)) return;
    setBusyId(q.id);
    try {
      await axios.delete(`${API}/enquiries/${q.id}`, { headers: authHeaders() });
      setEnquiries((list) => list.filter((e) => e.id !== q.id));
      toast.success("Enquiry deleted.");
    } catch (e) {
      toast.error("Could not delete. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const refundPayment = async (p) => {
    const already = p.refunded_amount || 0;
    const remaining = (p.amount - already).toFixed(2);
    const input = window.prompt(`Refund amount in euro for ${p.name} (€${remaining} remaining):`, remaining);
    if (input === null) return;
    const amount = parseFloat(input);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }
    setBusyId(p.session_id);
    try {
      const res = await axios.post(`${API}/payments/${p.session_id}/refund`, { amount }, { headers: authHeaders() });
      setPayments((list) => list.map((x) => (x.session_id === p.session_id ? { ...x, payment_status: res.data.status, status: res.data.status, refunded_amount: res.data.refunded_amount } : x)));
      toast.success(`€${amount.toFixed(2)} refunded — the client has been emailed.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-page" className="mx-auto max-w-3xl px-5 pb-24 pt-16 sm:px-8 lg:pt-20">
      {!token ? (
        <div className="mx-auto max-w-md">
          <Reveal y={12}><p className="eyebrow text-center">Private area</p></Reveal>
          <h1 className="mt-4 text-center font-serif text-4xl font-medium text-[#1E2723]">
            <MaskedLine delay={0.15}>Enquiries</MaskedLine>
          </h1>
          <Reveal delay={0.35}>
            <form onSubmit={login} className="soft-card mt-8 p-7" data-testid="admin-login-form">
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-[#2A3630]">Admin password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#65756C]" />
                <input
                  id="admin-password"
                  data-testid="admin-login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputCls} pl-11`}
                  placeholder="Enter your password"
                />
              </div>
              {error && <p className="mt-3 text-sm text-[#A34F39]" data-testid="admin-login-error">{error}</p>}
              <button type="submit" data-testid="admin-login-submit-button" disabled={checking} className="btn-primary mt-5 w-full disabled:opacity-60">
                {checking ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                {checking ? "Checking…" : "Sign in"}
              </button>
            </form>
          </Reveal>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow">Private area</p>
              <h1 className="mt-2 font-serif text-3xl font-medium text-[#1E2723] sm:text-4xl">Enquiries</h1>
            </div>
            <button onClick={logout} data-testid="admin-logout-button" className="btn-secondary !px-5 !py-2.5">
              <LogOut size={15} /> Sign out
            </button>
          </div>

          {enquiries === null ? (
            <div className="mt-16 flex justify-center"><Loader2 size={28} className="animate-spin text-[#527363]" /></div>
          ) : enquiries.length === 0 ? (
            <div className="soft-card mt-10 p-12 text-center" data-testid="admin-empty-state">
              <Inbox size={40} className="mx-auto text-[#65756C]" strokeWidth={1.5} />
              <p className="mt-4 text-sm text-[#54645B]">No enquiries yet — new submissions will appear here.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
              <p className="text-sm text-[#65756C]">
                {enquiries.filter((e) => !e.handled).length} to respond to · {enquiries.length} total, newest first
              </p>
              {enquiries.map((q) => {
                const pref = PREF_META[q.contact_preference];
                const busy = busyId === q.id;
                return (
                  <article
                    key={q.id}
                    className={`soft-card p-6 transition-opacity duration-300 ${q.handled ? "opacity-60" : ""}`}
                    data-testid={`admin-enquiry-${q.id.slice(0, 8)}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className={`font-serif text-xl font-medium text-[#1E2723] ${q.handled ? "line-through decoration-[#B5A793]" : ""}`}>{q.name}</h2>
                      <div className="flex items-center gap-2">
                        {q.handled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#E3ECE7] px-3 py-1 text-xs font-semibold text-[#527363]" data-testid={`admin-done-badge-${q.id.slice(0, 8)}`}>
                            <Check size={12} /> Responded
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                          q.session_type === "online" ? "bg-[#F8EBE7] text-[#B85C43]" : "bg-[#E3ECE7] text-[#527363]"
                        }`}>
                          {q.session_type === "online" ? <Video size={12} /> : <MapPin size={12} />}
                          {q.session_type === "online" ? "Online · €50" : "In person · €60"}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-[#65756C]">
                      {new Date(q.created_at).toLocaleString("en-IE", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    {q.message && (
                      <p className="mt-3 rounded-xl bg-[#F7F3EB] p-4 text-sm leading-relaxed text-[#3E4A43]">{q.message}</p>
                    )}
                    <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                      {q.availability && <p className="text-[#4A5750]"><span className="font-semibold text-[#2A3630]">Available:</span> {q.availability}</p>}
                      {pref && (
                        <p className="flex items-center gap-1.5 text-[#4A5750]">
                          <pref.icon size={14} className="text-[#B85C43]" /> <span className="font-semibold text-[#2A3630]">{pref.label}</span>
                        </p>
                      )}
                      <p><a href={`mailto:${q.email}`} className="font-medium text-[#527363] underline decoration-[#B5A793] underline-offset-2">{q.email}</a></p>
                      {q.phone && <p><a href={`tel:${q.phone.replace(/\s/g, "")}`} className="font-medium text-[#527363] underline decoration-[#B5A793] underline-offset-2">{q.phone}</a></p>}
                    </div>
                    <NoteEditor
                      enquiry={q}
                      authHeaders={authHeaders}
                      onSaved={(id, note) => setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, notes: note } : e)))}
                    />
                    <div className="mt-5 flex flex-wrap gap-2 border-t border-[#E2DACD] pt-4">
                      <button
                        onClick={() => toggleHandled(q)}
                        disabled={busy}
                        data-testid={`admin-toggle-done-${q.id.slice(0, 8)}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#527363] px-4 py-2 text-xs font-semibold text-[#527363] transition-colors hover:bg-[#E3ECE7] disabled:opacity-50"
                      >
                        {busy ? <Loader2 size={13} className="animate-spin" /> : q.handled ? <Undo2 size={13} /> : <Check size={13} />}
                        {q.handled ? "Mark as open" : "Mark as responded"}
                      </button>
                      <button
                        onClick={() => remove(q)}
                        disabled={busy}
                        data-testid={`admin-delete-${q.id.slice(0, 8)}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#B85C43] px-4 py-2 text-xs font-semibold text-[#B85C43] transition-colors hover:bg-[#F8EBE7] disabled:opacity-50"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="mt-14">
            <h2 className="font-serif text-2xl font-medium text-[#1E2723]" data-testid="admin-payments-heading">Payments</h2>
            {payments === null ? (
              <div className="mt-8 flex justify-center"><Loader2 size={24} className="animate-spin text-[#527363]" /></div>
            ) : payments.length === 0 ? (
              <p className="mt-4 text-sm text-[#54645B]" data-testid="admin-payments-empty">No payments yet — paid sessions will appear here.</p>
            ) : (
              <div className="mt-5 space-y-3" data-testid="admin-payments-list">
                {payments.map((p) => (
                  <div key={p.session_id} className="soft-card flex flex-wrap items-center justify-between gap-3 p-5" data-testid={`admin-payment-${p.session_id.slice(-8)}`}>
                    <div>
                      <p className="font-semibold text-[#1E2723]">{p.name}</p>
                      <p className="text-xs text-[#65756C]">
                        {p.lookup_key === "session_online" ? "Online session" : p.lookup_key === "custom_amount" ? "Session (agreed fee)" : "In-person session"} · {new Date(p.created_at).toLocaleString("en-IE", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                      <p className="text-xs text-[#65756C]">{p.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-serif text-xl font-medium text-[#1E2723]">€{p.amount?.toFixed(0)}</span>
                      {p.refunded_amount > 0 && (
                        <span className="text-xs text-[#65756C]">€{p.refunded_amount.toFixed(2)} refunded</span>
                      )}
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        p.payment_status === "paid" ? "bg-[#E3ECE7] text-[#527363]" : p.payment_status === "expired" ? "bg-[#F2ECE1] text-[#65756C]" : p.payment_status === "refunded" ? "bg-[#E6DEC3] text-[#8A6A2F]" : p.payment_status === "partially_refunded" ? "bg-[#F8EBE7] text-[#8A6A2F]" : "bg-[#F8EBE7] text-[#B85C43]"
                      }`}>
                        {p.payment_status === "paid" ? "Paid" : p.payment_status === "expired" ? "Expired" : p.payment_status === "refunded" ? "Refunded" : p.payment_status === "partially_refunded" ? "Part refunded" : "Pending"}
                      </span>
                      {(p.payment_status === "paid" || p.payment_status === "partially_refunded") && (
                        <button
                          onClick={() => refundPayment(p)}
                          disabled={busyId === p.session_id}
                          data-testid={`admin-refund-${p.session_id.slice(-8)}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#B85C43] px-3.5 py-1.5 text-xs font-semibold text-[#B85C43] transition-colors hover:bg-[#F8EBE7] disabled:opacity-50"
                        >
                          {busyId === p.session_id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Refund
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;
